import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { DOCUMENT_DATE_TIME_FORMAT } from '@app/app.constants';
import { PdfPreviewTemplateService } from '@app/modules/pdf-request-form/services/pdf-preview-template.service';
import { RunData } from '@app/modules/runs/runs.types';
import {
  ExportDialogDataType,
  ExportType,
  ExtendedProductInventoryItem,
  StatusResponse,
} from '@app/modules/service-data/types/service-data.types';
import {
  BlobUriResponseModel,
  DataInputType,
  ExporterState,
  RuntimeStatus,
} from '@core/core.types';
import { generatePassword } from '@core/utils/core.util';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { PasswordDialogComponent } from '@shared/components/password-dialog/password-dialog.component';
import {
  ProgressData,
  ProgressDialogComponent,
} from '@shared/components/progress/progress-dialog.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { WarningService } from '@shared/components/warning/warning-dialog.component';
import moment from 'moment';
import {
  BehaviorSubject,
  catchError,
  first,
  interval,
  map,
  Observable,
  of,
  switchMap,
  take,
  takeWhile,
  tap,
} from 'rxjs';
import { AccountUtilsService } from './account-utils.service';
import { ExportersService } from './exporters.service';
@Injectable({
  providedIn: 'root',
})
export class ExportersUtilsService {
  private datePipe = inject(DatePipe);
  private exportersService = inject(ExportersService);
  private accountUtils = inject(AccountUtilsService);
  private pdfPreviewTemplateService = inject(PdfPreviewTemplateService);
  private warningService = inject(WarningService);
  private translateService = inject(TranslateService);
  private ngbModal = inject(NgbModal);
  private toastService = inject(ToastService);

  public openProgressDialog(
    title: string,
    password?: string
  ): BehaviorSubject<ProgressData> {
    const progressSubject = new BehaviorSubject<ProgressData>({
      value: 0,
      status: 'shared.progress.preparingDocument',
    });

    const dialogRef = this.ngbModal.open(ProgressDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'modal-size-md',
    });

    dialogRef.componentInstance.title = title;
    dialogRef.componentInstance.password = password;
    dialogRef.componentInstance.progressSubject = progressSubject;

    progressSubject.subscribe((data) => {
      if (data.value === 100) {
        setTimeout(() => dialogRef.close(), 1000);
        if (password && data.success) {
          const modalRef = this.ngbModal.open(PasswordDialogComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
          });

          modalRef.componentInstance.password = password;
        }
      }
    });

    return progressSubject;
  }

  public export(
    filters: { [key: string]: string },
    type: ExportType,
    password?: string,
    anonymous?: boolean
  ): Observable<string> {
    const fileName = `${type}_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}${type === ExportType.BGIInformation ? '.zip' : '.xlsx'}`;

    return this.accountUtils.getRoleCountries$().pipe(
      take(1),
      switchMap((countries: string[]) =>
        this.exportersService.getCreateBlobUri(fileName).pipe(
          switchMap((response: BlobUriResponseModel) =>
            this.exportersService.exportNipts(
              type,
              response.uri,
              countries,
              filters,
              password,
              anonymous
            )
          ),
          catchError(() => of(RuntimeStatus.Failed)),
          switchMap((status: StatusResponse) =>
            this.download(fileName, status.InstanceId)
          )
        )
      )
    );
  }

  public exportSelected(
    exportDialogDataType: ExportDialogDataType,
    ids: string[],
    type: ExportType,
    password?: string,
    anonymous?: boolean
  ): Observable<string> {
    const fileName = `${type}_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}${type === ExportType.BGIInformation ? '.zip' : '.xlsx'}`;

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response: BlobUriResponseModel) =>
        this.exportersService.exportSelectedNipts(
          exportDialogDataType,
          ids,
          type,
          response.uri,
          password,
          anonymous
        )
      ),
      catchError(() => of(false)),
      switchMap((status: StatusResponse) =>
        this.download(fileName, status.InstanceId)
      )
    );
  }

  public getRequestFormTemplate(
    pii: ExtendedProductInventoryItem,
    previousSampleKitId: string,
    { index },
    selectedDataInputTypes: DataInputType[],
    fieldSet: string[],
    sampleFieldSet: string[]
  ) {
    if (pii) {
      return {
        content: this.pdfPreviewTemplateService.createTemplate({
          pii,
          previousSampleKitId,
          selectedDataInputTypes,
          fieldSet,
          sampleFieldSet,
        }),
        filename: `${index + 1}-${pii.kitId}.pdf`,
      };
    }
    return null;
  }

  public exportKits(
    previousMonthOnly: boolean,
    name: string,
    filenameExportType: string
  ): Observable<string> {
    const fileName = `Kits_${filenameExportType}_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}.xlsx`;

    const progressSubject = this.openProgressDialog(name);

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response) =>
        this.accountUtils.getLaboratory$().pipe(
          first(),
          switchMap((laboratory: string) =>
            this.exportersService.inPartnerWarehouseExport(
              response.uri,
              laboratory,
              // If monthly export, pass from / to params
              ...(previousMonthOnly
                ? [
                    moment().add(-1, 'month').startOf('month').toISOString(),
                    moment().add(-1, 'month').endOf('month').toISOString(),
                  ]
                : [])
            )
          )
        )
      ),
      switchMap((status: StatusResponse) =>
        this.download(fileName, status.InstanceId)
      ),
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public exportPartnerDoctors(filters: {
    [key: string]: string;
  }): Observable<string> {
    const fileName = `Partners_doctors_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}.xlsx`;

    const progressSubject = this.openProgressDialog(
      'partnersPage.buttons.exportDoctors'
    );

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response: BlobUriResponseModel) =>
        this.exportersService.doctorsByPartnersExport(response.uri, filters)
      ),
      switchMap((status: StatusResponse) =>
        this.download(fileName, status.InstanceId)
      ),
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          if (status === RuntimeStatus.Failed) {
            const { title, message } = this.translateService.instant(
              'partnersPage.errors.fileDoesNotExist'
            );
            this.warningService.showWarning({ title, message });
          }

          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public exportPartners(filters: {
    [key: string]: string;
  }): Observable<string> {
    const fileName = `Partners_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}.xlsx`;

    const progressSubject = this.openProgressDialog(
      'partnersPage.buttons.exportPartners'
    );

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response: BlobUriResponseModel) =>
        this.exportersService.allPartnersExport(response.uri, filters)
      ),
      switchMap((status: StatusResponse) =>
        this.download(fileName, status.InstanceId)
      ),
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          if (status === RuntimeStatus.Failed) {
            const { title, message } = this.translateService.instant(
              'partnersPage.errors.fileDoesNotExist'
            );
            this.warningService.showWarning({ title, message });
          }

          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public exportOnlinePartners(filters: {
    [key: string]: string;
  }): Observable<string> {
    const fileName = `Online_Partners_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}.xlsx`;

    const progressSubject = this.openProgressDialog(
      'partnersPage.buttons.exportOnlinePartners'
    );

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response: BlobUriResponseModel) =>
        this.exportersService.onlinePartnersExport(response.uri, filters)
      ),
      switchMap((status: StatusResponse) =>
        this.download(fileName, status.InstanceId)
      ),
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          if (status === RuntimeStatus.Failed) {
            const { title, message } = this.translateService.instant(
              'partnersPage.errors.fileDoesNotExist'
            );
            this.warningService.showWarning({ title, message });
          }

          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public exportDoctors(doctorIds: string[]): Observable<string> {
    const fileName = `Doctors_${this.datePipe.transform(
      new Date(),
      DOCUMENT_DATE_TIME_FORMAT
    )}.xlsx`;

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      switchMap((response: BlobUriResponseModel) =>
        this.exportersService.doctorsExport(doctorIds, response.uri)
      ),
      switchMap((status: StatusResponse) =>
        this.download(fileName, status.InstanceId)
      )
    );
  }

  public exportBgiAndInfoSheet(
    piiIds: string[],
    sampleIds: string[]
  ): Observable<string> {
    const progressSubject = this.openProgressDialog(
      'dashboardPage.buttons.bgiInfosheetExport'
    );

    return this.exportSelected(
      ExportDialogDataType.Pii,
      piiIds,
      ExportType.BGIInformation
    ).pipe(
      switchMap((status: string) => {
        if (status !== RuntimeStatus.Completed) {
          return of(status);
        }

        return this.exportSelected(
          ExportDialogDataType.Sample,
          sampleIds,
          ExportType.Information
        );
      }),
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public exportRuns(exportType: ExportType, runData: RunData[]) {
    const fileName = `${exportType} Information sheet_${runData
      .map((run) => run.run.name)
      .join(
        '_'
      )}_${this.datePipe.transform(new Date(), DOCUMENT_DATE_TIME_FORMAT)}.xlsx`.replace(
      /[^a-zA-Z0-9._ -]/g,
      ''
    );

    const password =
      exportType === ExportType.BgiPlasma ? generatePassword() : undefined;

    const progressSubject = this.openProgressDialog(
      this.translateService.instant(
        'runsPage.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      ),
      password
    );

    return this.exportersService.getCreateBlobUri(fileName).pipe(
      catchError(() => {
        this.toastService.danger(
          this.translateService.instant(
            'core.exporters.runs.errors.createBlob'
          ),
          this.translateService.instant('core.exporters.runs.errors.title')
        );
        return of(null);
      }),
      switchMap((response: BlobUriResponseModel) => {
        if (exportType === ExportType.Halos) {
          return this.exportersService.halosExport(
            response.uri,
            runData.map((runD) => runD.run.id)
          );
        } else if (exportType === ExportType.HalosPro) {
          return this.exportersService.halosProExport(
            response.uri,
            runData.map((runD) => runD.run.id)
          );
        } else if (exportType === ExportType.Plasma) {
          return this.exportersService.plasmaExport(
            response.uri,
            runData[0].run.id
          );
        } else if (exportType === ExportType.BgiPlasma) {
          return this.exportersService.bgiPlasmaExport(
            response.uri,
            runData[0].run.id,
            password
          );
        } else if (exportType === ExportType.Tat) {
          return this.exportersService.tatExport(
            response.uri,
            runData.map((runD) => runD.run.id)
          );
        }

        return of(null);
      }),
      catchError(() => {
        this.toastService.danger(
          this.translateService.instant('core.exporters.runs.errors.failed'),
          this.translateService.instant('core.exporters.runs.errors.title')
        );
        return of(null);
      }),
      switchMap((status: StatusResponse) => {
        if (!status) {
          return of(RuntimeStatus.Failed);
        }

        return this.download(fileName, status.InstanceId);
      }),
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          if (status === RuntimeStatus.Failed) {
            this.toastService.danger(
              this.translateService.instant(
                'core.exporters.runs.errors.runtime'
              ),
              this.translateService.instant('core.exporters.runs.errors.title')
            );
          }
          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public download(fileName: string, instanceId: string): Observable<string> {
    return this.exportersService.getReadBlobUri(fileName).pipe(
      switchMap((blobUri: BlobUriResponseModel) =>
        interval(5000).pipe(
          switchMap(() =>
            this.exportersService.checkExporterStatus(instanceId)
          ),
          catchError(() => of(RuntimeStatus.Failed)),
          takeWhile(
            (state: ExporterState) =>
              state.runtimeStatus === RuntimeStatus.Running,
            true
          ),
          map((state: ExporterState) => {
            if (state.runtimeStatus === RuntimeStatus.Completed) {
              window.location.href = blobUri.uri;
            }

            return state.runtimeStatus;
          })
        )
      )
    );
  }
}
